
/* eslint-disable max-lines,no-fallthrough */

import {
  Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import {
  FORM_FIELD_SELECT,
  FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FILTER,
  FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID, OrganizationSubTypeOption,
} from 'src/app/@shared/form/form-field-config';
import {
  FilterFormFieldKey,
  FormConfig, FormFieldKey, FormFieldList, FormFieldValue,
  FormFieldsetConfig, OrganizationFilterForm, PublicFilterForm,
  SFormConfig,
} from 'src/app/@shared/form/form.types';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormKey } from 'src/app/@shared/form/form-config';
import { FormMainType, Qualification } from '@lib/types';
import { ModalController, ModalOptions } from '@ionic/angular';

import { FORM_FIELD_OPTIONS, QualificationSegment, getQualificationOptions } from '@data/form-field-options';
import { OrganizationSource } from 'src/app/organization/@shared/organization.model';
import { OrganizationSubType, OrganizationSubTypeForm } from '@api/organization/@shared/organization.entity';
import { publicFilterFieldsConfig } from '@api/organization/@shared/public-fields.config';
import {
  isInternalOrganizationFilterFieldAvailable,
  SUB_TYPE_GATED_ORGANIZATION_FILTER_FIELDS,
} from 'src/app/@shared/organization-subtype-gated-filter-fields';
import {
  createFieldLists,
  createFieldsByConfig,
  createForm,
  getConfigs,
} from 'src/app/@shared/form/form-helper';
import {
  getControlValueByConfig,
  normalizeFilterResultByConfig,
} from 'src/app/@shared/filter-form-value.adapter';

export type FilterResult<U> = { [key in keyof U]?: FormFieldValue | FormFieldValue[] };
type SelectModalItem = { id: string; title: string; displayName: string };
export type FilterModalMeta = {
  selectedTopics?: SelectModalItem[];
};
const PUBLIC_FILTER_EXCLUDED_SUB_TYPE: OrganizationSubType = 'selfHelpAddressOther';
const PUBLIC_FILTER_SUB_TYPES: OrganizationSubType[] = [
  'selfHelpAddressRa',
  'selfHelpAddressGa',
  'selfHelpAddressCommunity',
  'selfHelpAddressShGroup',
  'selfHelpAddressBa',
];
const LOCATION_SEARCH_DISABLED_SUB_TYPE_FORM: OrganizationSubTypeForm = 'selfHelpAddressRa1c';

@Component({
  selector: 'page-filter',
  standalone: false,
  styleUrls: ['./filter.page.scss'],
  templateUrl: './filter.page.html',
})
export class FilterPage<U> implements OnChanges {
  @Input() public appliedWhereFilter: FilterResult<U> = {};
  @Input() public formId?: FormKey;
  @Input() public formType: FormMainType;
  @Input() public isInline: boolean = false;
  @Input() public listSubId?: OrganizationSubTypeOption;
  @Input() public listSubTypeForm?: OrganizationSubTypeForm;
  @Input() public locationSearchActive: boolean = false;
  @Input() public topicLabelById: Record<string, string> = {};

  @Input() public presentingElement: HTMLIonModalElement;
  @Output() public filterChanged = new EventEmitter<FilterResult<U> & FilterModalMeta>();

  public filterForm: FormGroup<{ [key in FilterFormFieldKey]?: any }> = new FormGroup({});
  public formFieldLists: FormFieldList[] = [];

  private filterFormConfigByListType: FormConfig;
  private baseOrganizationFilterFormConfig: SFormConfig<keyof OrganizationFilterForm>;
  private fieldsetConfig: FormFieldsetConfig;
  private activeFormId: FormKey;

  private modalController = inject(ModalController);
  private modalIsOpen = false;
  private isInitialized = false;

  private selectedTopics: SelectModalItem[] = [];

  public async dismiss(data?: FilterResult<U>): Promise<boolean> {
    if (this.isInline) {
      return false;
    }

    if (this.presentingElement) {
      this.presentingElement.canDismiss = true;
    } else {
      console.error('[FilterPage] No presenting element found!');
    }

    if (data) {
      return await this.modalController.dismiss(data, 'apply');
    }

    return await this.modalController.dismiss(null, 'cancel');
  }

  // workaround for having typed variables in ng-template
  // (see https://stackoverflow.com/questions/55458421/ng-template-typed-variable)
  public asFormControl(fieldId: FormFieldKey): FormControl {
    return this.filterForm.get(fieldId) as FormControl;
  }

  public save(type: 'apply' | 'reset'): void {
    const filterResult = this.getFilterPayload();

    const payload = type === 'apply'
      ? filterResult
      : {};

    if (this.isInline) {
      this.filterChanged.emit(payload as FilterResult<U> & FilterModalMeta);

      return;
    }

    this.dismiss(payload).catch();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.isInitialized || !changes.appliedWhereFilter) {
      return;
    }

    this.selectedTopics = [];
    this.filterForm.reset();
    this.patchInitialValues();
    this.filterForm.markAsPristine();
  }

  // eslint-disable-next-line max-statements
  public ngOnInit(): void {
    const formId = this.formId || this.getFormIdByFormType();

    this.activeFormId = formId;

    const { formConfig, fieldsetConfig } = getConfigs(formId); // get initial configs first

    switch (formId) {
      case 'filterOrganization':

      case 'filterPublicOrganization':
      {
        const appliedWhereFilter = this.appliedWhereFilter as FilterResult<OrganizationSource>;
        const subTypeOption = appliedWhereFilter.subType as OrganizationSubTypeOption[] || [];
        const subTypeForm = appliedWhereFilter.subTypeForm as OrganizationSubTypeForm[] || [];
        const initialSubTypes = subTypeOption.length
          ? subTypeOption
          : [];
        const inferredSubTypes = this.getSubTypesBySubTypeForms(subTypeForm);
        const normalizedSubTypes = [...new Set(this.normalizeSubTypes(initialSubTypes).concat(inferredSubTypes))]
          .filter((subType) => {
            return formId !== 'filterPublicOrganization' || subType !== PUBLIC_FILTER_EXCLUDED_SUB_TYPE;
          });

        if (!normalizedSubTypes.length && this.listSubId && this.listSubId !== 'all') {
          normalizedSubTypes.push(this.listSubId);
        }

        this.baseOrganizationFilterFormConfig = formConfig;
        this.filterFormConfigByListType = this.getOrganizationFilterConfig(this.baseOrganizationFilterFormConfig,
          this.formType,
          normalizedSubTypes,
          subTypeForm);
        break;
      }

      default:
        console.warn('using initial filter config', formId);
        this.filterFormConfigByListType = formConfig;
    }

    this.fieldsetConfig = fieldsetConfig;

    const fields = createFieldsByConfig(this.filterFormConfigByListType);

    this.filterForm = createForm(formId, fields);
    this.formFieldLists = createFieldLists(formId, fields, fieldsetConfig);
    this.patchInitialValues();
    this.isInitialized = true;
  }

  public onValueUpdated(value: FormFieldValue, item: FormFieldModel): void {
    if (item.id === 'subType') {
      this.onOrganizationSubTypeUpdated(value as OrganizationSubTypeOption | OrganizationSubTypeOption[]);
    }

    if (item.id === 'subTypeForm') {
      this.onOrganizationSubTypeFormUpdated(this.normalizeSubTypeForms(value));
    }

    if (this.isInline) {
      this.emitInlineFilterChanged();
    }
  }

  public async openModal(result: { field: FormFieldModel }): Promise<void> {
    if (this.modalIsOpen) {
      return Promise.resolve();
    }

    if (result.field.id !== 'topics') {
      console.warn(`[FilterPage] Unsupported modal field: ${result.field.id}`);

      return Promise.resolve();
    }

    this.modalIsOpen = true;

    // Use clone to avoid directly mutating the selectedTopics
    // Fixes issues arising when selecting/deselecting topics and then cancelling the modal
    const modal = await this.getTopicModal(this.formType, this.cloneSelectedTopics(this.selectedTopics), true);

    await modal.present();

    const { data } = await modal.onWillDismiss();

    this.modalIsOpen = false;

    if (!data) {
      return Promise.resolve();
    }

    this.selectedTopics = data as SelectModalItem[];

    this.filterForm.patchValue({
      topics: this.selectedTopics.length
        ? this.selectedTopics as any
        : null,
    });
    this.filterForm.markAsDirty();

    if (this.isInline) {
      this.emitInlineFilterChanged();
    }

    return Promise.resolve();
  }

  private getFormIdByFormType(): FormKey {
    switch (this.formType) {
      case 'selfHelpAddress':

      case 'otherAddress':
        return 'filterOrganization';

      case 'person':
        return 'filterPerson';

      case 'topic':
        return 'filterTopic';

      default:
        console.warn('no custom filter config found', this.formType);

        return 'filterDefault';
    }
  }

  private getFilterPayload(): FilterResult<U> & FilterModalMeta {
    const filterResult: FilterResult<U> & FilterModalMeta = this.filterForm.value || {};
    const filterFormConfig = this.filterFormConfigByListType;
    const normalizedFilterResultByConfig = normalizeFilterResultByConfig(filterResult as Record<string, unknown>, filterFormConfig);
    const normalizedFilterResult = normalizedFilterResultByConfig as FilterResult<U> & FilterModalMeta;

    if (this.isOrganizationSourceFilter(normalizedFilterResult)) {
      normalizedFilterResult.topics = this.selectedTopics.map((topic) => {
        return topic.id;
      }) as any;
      normalizedFilterResult.selectedTopics = this.selectedTopics;
    }

    return normalizedFilterResult;
  }

  private emitInlineFilterChanged(): void {
    this.filterChanged.emit(this.getFilterPayload());
  }

  private patchInitialValues(): void {
    for (const key of Object.keys(this.filterForm.value)) {
      let value: FormFieldValue;

      if (key === 'subType') {
        const organizationAppliedWhereFilter = this.appliedWhereFilter as FilterResult<OrganizationSource>;
        const inferredSubTypes = this.getSubTypesBySubTypeForms(organizationAppliedWhereFilter.subTypeForm as OrganizationSubTypeForm[] || []);
        const appliedSubType = this.appliedWhereFilter[key] as OrganizationSubTypeOption | OrganizationSubTypeOption[];
        const selectedSubTypesFromFilter = this.normalizeSubTypes(appliedSubType);
        const selectedSubTypes = [...new Set(selectedSubTypesFromFilter.concat(inferredSubTypes))];

        if (!selectedSubTypes.length && this.listSubId && this.listSubId !== 'all') {
          selectedSubTypes.push(this.listSubId);
        }

        value = selectedSubTypes as any;
      } else if (key === 'topics' && Array.isArray(this.appliedWhereFilter[key])) {
        this.selectedTopics = (this.appliedWhereFilter[key] as string[]).map((topicId) => {
          const title = this.topicLabelById[topicId] || topicId;

          return {
            displayName: title,
            id: topicId,
            title,
          } as SelectModalItem;
        });
        value = this.selectedTopics.length
          ? this.selectedTopics as any
          : null;
      } else if (typeof this.appliedWhereFilter[key]?.search === 'string') {
        value = this.appliedWhereFilter[key].search;
      } else if (this.appliedWhereFilter[key]?.contains) {
        value = this.appliedWhereFilter[key].contains;
      } else {
        value = getControlValueByConfig(this.appliedWhereFilter[key], this.filterFormConfigByListType[key]) as FormFieldValue;
      }

      this.filterForm.patchValue({
        [key]: value,
      });
    }
  }

  // organization only!
  //
  // list view "all self help addresses"
  // we want so show subTypeform field when specific sub type has been set
  private onOrganizationSubTypeUpdated(subType: OrganizationSubTypeOption | OrganizationSubTypeOption[],
    subTypeForm?: OrganizationSubTypeForm[]): void {
    const selectedSubTypes = this.normalizeSubTypes(subType);
    const validSubTypeForms = this.getSubTypeFormsBySubTypes(selectedSubTypes);
    const previousSelectedSubTypeForms = subTypeForm
      || this.normalizeSubTypeForms(this.filterForm.getRawValue().subTypeForm as FormFieldValue | FormFieldValue[]);
    const selectedSubTypeForms = selectedSubTypes.length
      ? previousSelectedSubTypeForms.filter((item) => {
        return validSubTypeForms.includes(item);
      })
      : [];
    const newConfig
      = this.getOrganizationFilterConfig(this.baseOrganizationFilterFormConfig, this.formType, selectedSubTypes, subTypeForm || selectedSubTypeForms);
    const fields = createFieldsByConfig(newConfig);

    this.filterFormConfigByListType = newConfig;
    this.rebuildFilterForm(fields);
    this.formFieldLists = createFieldLists(this.activeFormId, fields, this.fieldsetConfig);

    // make sure subTypeform value is initially empty
    this.filterForm.patchValue({
      qualifications: [],
      subTypeForm: selectedSubTypeForms,
    });
  }

  // organization only!
  //
  private onOrganizationSubTypeFormUpdated(subTypeForm: OrganizationSubTypeForm[]): void {
    const selectedSubTypes = this.normalizeSubTypes(this.filterForm.getRawValue().subType as OrganizationSubTypeOption | OrganizationSubTypeOption[]);
    const inferredSubTypes = this.getSubTypesBySubTypeForms(subTypeForm);
    const mergedSubTypes = selectedSubTypes.concat(inferredSubTypes.filter((subType) => {
      return !selectedSubTypes.includes(subType);
    }));
    const newConfig
      = this.getOrganizationFilterConfig(this.baseOrganizationFilterFormConfig, this.formType, mergedSubTypes, subTypeForm);
    const fields = createFieldsByConfig(newConfig);

    this.filterFormConfigByListType = newConfig;
    this.rebuildFilterForm(fields);
    this.formFieldLists = createFieldLists(this.activeFormId, fields, this.fieldsetConfig);

    // make sure subTypeform value is initially empty
    this.filterForm.patchValue({
      qualifications: [],
      subType: mergedSubTypes,
    });
  }

  // eslint-disable-next-line max-statements,max-lines-per-function
  private getOrganizationFilterConfig(baseConfig: SFormConfig<keyof OrganizationFilterForm | keyof PublicFilterForm>,
    formType: FormMainType, subTypeOptions: OrganizationSubType[],
    subTypeForms: OrganizationSubTypeForm[] = []): SFormConfig<keyof OrganizationFilterForm | keyof PublicFilterForm> {
    const config: SFormConfig<keyof OrganizationFilterForm | keyof PublicFilterForm> = {
      ...baseConfig,
    };

    if (baseConfig.qualifications) {
      config.qualifications = {
        ...FORM_FIELD_SELECT,
        hasMultipleValues: true,
        hasTranslation: false,
        options: this.getAvailableQualifications(formType, subTypeOptions, subTypeForms),
      };
    }

    // type 7: record is always public, no subType, no subTypeforms
    if (formType !== 'selfHelpAddress') {
      const skippedFields: (keyof OrganizationFilterForm)[] = [
        'subType',
        'subTypeForm',
        'isPublished',
        'hasTransparencyLabel',
        ...SUB_TYPE_GATED_ORGANIZATION_FILTER_FIELDS,
      ];

      for (const key of skippedFields) {
        delete config[key];
      }

      return config;
    }

    if (this.listSubId === 'all') {
      config.subType.options = this.activeFormId === 'filterPublicOrganization'
        ? FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FILTER.filter((subType) => {
          return subType !== PUBLIC_FILTER_EXCLUDED_SUB_TYPE;
        })
        : FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FILTER;
    } else {
      delete config.subType; // already filtered via listSubId
    }

    if (this.listSubTypeForm) {
      delete config.subTypeForm;
    } else {
      config.subTypeForm = {
        ...config.subTypeForm,
        options: this.getSubTypeFormsBySubTypes(subTypeOptions),
      };

      if (this.locationSearchActive) {
        const disabledOptions = config.subTypeForm.disabledOptions || [];

        config.subTypeForm.disabledOptions = [...disabledOptions, LOCATION_SEARCH_DISABLED_SUB_TYPE_FORM];
      }
    }

    if (subTypeOptions.length === 1 && subTypeOptions[0] === 'selfHelpAddressOther') {
      delete config.isPublished;
      delete config.hasTransparencyLabel;

      return config;
    }

    if (this.activeFormId === 'filterPublicOrganization') {
      this.removeUnavailablePublicFilterFields(config, subTypeOptions);
    }

    if (this.activeFormId === 'filterOrganization') {
      this.removeUnavailableInternalFilterFields(config, subTypeOptions);
    }

    if (!this.shouldShowTransparencyLabelFilter(subTypeOptions, subTypeForms)) {
      delete config.hasTransparencyLabel;
    }

    return config;
  }

  private removeUnavailablePublicFilterFields(config: SFormConfig<keyof OrganizationFilterForm | keyof PublicFilterForm>,
    subTypeOptions: OrganizationSubType[]): void {
    const relevantSubTypes = subTypeOptions.length
      ? subTypeOptions.filter((subType) => {
        return subType !== PUBLIC_FILTER_EXCLUDED_SUB_TYPE;
      })
      : PUBLIC_FILTER_SUB_TYPES;

    for (const key of Object.keys(config) as (keyof PublicFilterForm)[]) {
      const isVisible = key === 'subType' || relevantSubTypes.some((subType) => {
        return publicFilterFieldsConfig[subType]?.includes(key);
      });

      if (!isVisible) {
        delete config[key];
      }
    }
  }

  private removeUnavailableInternalFilterFields(config: SFormConfig<keyof OrganizationFilterForm | keyof PublicFilterForm>,
    subTypeOptions: OrganizationSubType[]): void {
    if (!subTypeOptions.length) {
      return;
    }

    for (const key of SUB_TYPE_GATED_ORGANIZATION_FILTER_FIELDS) {
      if (!isInternalOrganizationFilterFieldAvailable(key, subTypeOptions)) {
        delete config[key];
      }
    }
  }

  private rebuildFilterForm(fields: ReturnType<typeof createFieldsByConfig>): void {
    const currentValue = this.filterForm.getRawValue();
    const wasDirty = this.filterForm.dirty;

    this.filterForm = createForm(this.activeFormId, fields);
    this.filterForm.patchValue(currentValue);

    if (wasDirty) {
      this.filterForm.markAsDirty();
    }
  }

  private normalizeSubTypes(value: OrganizationSubTypeOption | OrganizationSubTypeOption[]): OrganizationSubType[] {
    const values = Array.isArray(value)
      ? value
      : [value];

    return values.filter((item): item is OrganizationSubType => {
      return !!item && item !== 'all';
    });
  }

  private getSubTypeFormsBySubTypes(subTypeOptions: OrganizationSubType[]): OrganizationSubTypeForm[] {
    if (!subTypeOptions?.length) {
      return FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID.all;
    }

    return [
      ...new Set(subTypeOptions.flatMap((subType) => {
        return FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID[subType];
      })),
    ];
  }

  private normalizeSubTypeForms(value: FormFieldValue | FormFieldValue[]): OrganizationSubTypeForm[] {
    if (!value) {
      return [];
    }

    const values = Array.isArray(value)
      ? value
      : [value];

    return values.filter((item): item is OrganizationSubTypeForm => {
      return typeof item === 'string';
    });
  }

  private shouldShowTransparencyLabelFilter(subTypeOptions: OrganizationSubType[], subTypeForms: OrganizationSubTypeForm[] = []): boolean {
    if (this.formType !== 'selfHelpAddress') {
      return false;
    }

    if (this.listSubId === 'selfHelpAddressGa') {
      return true;
    }

    if (this.listSubId && this.listSubId !== 'all') {
      return false;
    }

    if (this.listSubTypeForm) {
      return FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID.selfHelpAddressGa.includes(this.listSubTypeForm);
    }

    if (!subTypeOptions.length && !subTypeForms.length) {
      return true;
    }

    if (subTypeOptions.includes('selfHelpAddressGa')) {
      return true;
    }

    return subTypeForms.some((subTypeForm) => {
      return FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID.selfHelpAddressGa.includes(subTypeForm);
    });
  }

  private async getTopicModal(type: string, selectedItems: SelectModalItem[], isMultiSelect: boolean): Promise<HTMLIonModalElement> {
    const { TopicListPage } = await import('src/app/topic/topic-list/topic-list.page');
    const componentProps = {
      formType: type,
      isModal: true,
      isMultiSelect,
      selectedItems,
    };

    const options: ModalOptions = {
      component: TopicListPage,
      componentProps,
      cssClass: 'proto-modal proto-modal--list',
      mode: 'md',
    };

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');

    const modal = await this.modalController.create(options);

    options.componentProps.presentingElement = modal;

    return modal;
  }

  private getAvailableQualifications(type: FormMainType,
    subTypes: OrganizationSubType[] = [],
    subTypeForms: OrganizationSubTypeForm[] = []): Qualification[] {
    const inferredSubTypes = this.getSubTypesBySubTypeForms(subTypeForms);
    const qualificationSegments = [
      ...new Set(inferredSubTypes.concat(subTypes)
        .map((subType) => {
          return this.getQualificationSegmentBySubType(subType);
        })
        .filter(Boolean)),
    ] as QualificationSegment[];

    console.info('availableQualifications', subTypes, subTypeForms, qualificationSegments);

    if (qualificationSegments.length) {
      const options = qualificationSegments
        .flatMap((qualificationType) => {
          return getQualificationOptions(qualificationType);
        });

      return [...new Set(options)].sort();
    }

    if (type === 'otherAddress') {
      return getQualificationOptions('type67');
    }

    if (type === 'person') {
      return getQualificationOptions('type8');
    }

    return FORM_FIELD_OPTIONS.qualifications as unknown as Qualification[];
  }

  private getSubTypesBySubTypeForms(subTypeForms: OrganizationSubTypeForm[]): OrganizationSubType[] {
    if (!subTypeForms.length) {
      return [];
    }

    return (Object.entries(FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID) as [OrganizationSubTypeOption, OrganizationSubTypeForm[]][])
      .filter(([subType, forms]) => {
        return subType !== 'all'
          && forms.length
          && subTypeForms.some((subTypeForm) => {
            return forms.includes(subTypeForm);
          });
      })
      .map(([subType]) => {
        return subType as OrganizationSubType;
      });
  }

  private getQualificationSegmentBySubType(subType: OrganizationSubType): QualificationSegment | null {
    if (!subType) {
      return null;
    }

    if (subType.startsWith('selfHelpAddressRa')) {
      return 'type1';
    }

    if (subType.startsWith('selfHelpAddressGa')
      || subType.startsWith('selfHelpAddressCommunity')
      || subType.startsWith('selfHelpAddressShGroup')) {
      return 'type234';
    }

    if (subType === 'selfHelpAddressBa') {
      return 'type5';
    }

    if (subType === 'selfHelpAddressOther') {
      return 'type67';
    }

    return null;
  }

  private isOrganizationSourceFilter(result: FilterResult<U> & FilterModalMeta): result is FilterResult<OrganizationSource> & FilterModalMeta {
    return this.formType === 'selfHelpAddress' || this.formType === 'otherAddress';
  }

  // eslint-disable-next-line class-methods-use-this
  private cloneSelectedTopics(topics: SelectModalItem[]): SelectModalItem[] {
    return topics.map((topic) => {
      return {
        displayName: topic.displayName,
        id: topic.id,
        title: topic.title,
      };
    });
  }
}
